import axios, { InternalAxiosRequestConfig } from "axios";
import queryString from "query-string";
import { getAgentPlatformUrl } from "./axiosClient";
import { currentSession } from "../utils/user-auth-provider";

const agentPlatformClient = axios.create({
  baseURL: getAgentPlatformUrl(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
  paramsSerializer: (params) => queryString.stringify(params),
});

agentPlatformClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const session = await currentSession();
      if (session?.tokens?.accessToken) {
        config.headers.set(
          "Authorization",
          `Bearer ${session.tokens.accessToken.toString()}`
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "Failed to get Cognito session for agent platform request:",
        error
      );
    }
    return config;
  }
);

agentPlatformClient.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      return response.data;
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 401) {
      const errorConfig = error.config as InternalAxiosRequestConfig & {
        _retryCount?: number;
      };
      const retryCount = errorConfig._retryCount || 0;
      const maxRetries = 3;

      if (retryCount >= maxRetries) {
        // eslint-disable-next-line no-console
        throw error.response || error;
      }

      const retriedConfig = {
        ...errorConfig,
        _retryCount: retryCount + 1,
      };

      try {
        const session = await currentSession();
        if (session?.tokens?.accessToken) {
          const apiConfig = {
            ...retriedConfig,
            headers: {
              ...retriedConfig.headers,
              Authorization: `Bearer ${session.tokens.accessToken.toString()}`,
            },
          };
          return agentPlatformClient(apiConfig);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh token:", err);
      }
    }
    throw error.response || error;
  }
);

export default agentPlatformClient;
